import { Edit as EditIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { OrderItemDetail } from "../../../types/admin";

interface OrderItemEditModalProps {
  open: boolean;
  onClose: () => void;
  orderItem: {
    id: string;
    title: string;
    price?: number;
    quantity?: number;
    retailer?: string;
    notes?: string;
    trackingNumber?: string;
    retailerOrderId?: string;
    retailerTrackingId?: string;
    flightNumber?: string;
    estimatedDelivery?: string;
    cargoWeight?: number;
    itemCarrierCargoFee?: number;
  } | null;
  onSave: (orderItemId: string, updates: Partial<OrderItemDetail>) => Promise<void>;
}

const OrderItemEditModal: React.FC<OrderItemEditModalProps> = ({
  open,
  onClose,
  orderItem,
  onSave,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<Partial<OrderItemDetail>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (orderItem) {
      setFormData({
        price: orderItem.price ?? undefined,
        quantity: orderItem.quantity ?? undefined,
        retailer: orderItem.retailer || "",
        notes: orderItem.notes || "",
        trackingNumber: orderItem.trackingNumber || "",
        retailerOrderId: orderItem.retailerOrderId || "",
        retailerTrackingId: orderItem.retailerTrackingId || "",
        flightNumber: orderItem.flightNumber || "",
        estimatedDelivery: orderItem.estimatedDelivery || "",
        cargoWeight: orderItem.cargoWeight ?? undefined,
        itemCarrierCargoFee: orderItem.itemCarrierCargoFee ?? undefined,
      });
    }
  }, [orderItem]);

  const handleSubmit = async () => {
    if (!orderItem) return;

    try {
      setLoading(true);
      setError(null);

      await onSave(orderItem.id, formData);

      // Reset form and close modal
      setFormData({});
      onClose();
    } catch (error) {
      console.error("Error updating item:", error);
      setError(t("admin.failedToUpdateItem"));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({});
    setError(null);
    onClose();
  };

  const handleInputChange = (field: keyof OrderItemDetail, value: string | number | undefined) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <EditIcon />
          {t("admin.editOrderItem")}
        </Box>
      </DialogTitle>

      <DialogContent>
        {orderItem && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {orderItem.title}
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("common.price")}
              type="number"
              value={formData.price ?? ""}
              onChange={(e) => {
                const value = e.target.value === "" ? undefined : parseFloat(e.target.value);
                handleInputChange("price", value);
              }}
              disabled={loading}
              inputProps={{ step: "0.01", min: "0" }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("admin.orders.quantity")}
              type="number"
              value={formData.quantity ?? ""}
              onChange={(e) => {
                const value = e.target.value === "" ? undefined : parseInt(e.target.value, 10);
                handleInputChange("quantity", value);
              }}
              disabled={loading}
              inputProps={{ step: "1", min: "1" }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("admin.retailer")}
              value={formData.retailer || ""}
              onChange={(e) => handleInputChange("retailer", e.target.value)}
              disabled={loading}
              placeholder={t("admin.retailerPlaceholder")}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("admin.trackingNumber")}
              value={formData.trackingNumber || ""}
              onChange={(e) =>
                handleInputChange("trackingNumber", e.target.value)
              }
              disabled={loading}
              placeholder={t("admin.trackingNumberPlaceholder")}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("admin.retailerOrderId")}
              value={formData.retailerOrderId || ""}
              onChange={(e) =>
                handleInputChange("retailerOrderId", e.target.value)
              }
              disabled={loading}
              placeholder={t("admin.retailerOrderIdPlaceholder")}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("admin.retailerTrackingId")}
              value={formData.retailerTrackingId || ""}
              onChange={(e) =>
                handleInputChange("retailerTrackingId", e.target.value)
              }
              disabled={loading}
              placeholder={t("admin.retailerTrackingIdPlaceholder")}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("admin.orders.flightNumber")}
              value={formData.flightNumber || ""}
              onChange={(e) =>
                handleInputChange("flightNumber", e.target.value)
              }
              disabled={loading}
              placeholder={t("admin.orders.flightNumberPlaceholder")}
              inputProps={{ maxLength: 50 }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("admin.estimatedDelivery")}
              type="date"
              value={formData.estimatedDelivery || ""}
              onChange={(e) =>
                handleInputChange("estimatedDelivery", e.target.value)
              }
              disabled={loading}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("admin.cargoWeight")}
              type="number"
              value={formData.cargoWeight ?? ""}
              onChange={(e) => {
                const value = e.target.value === "" ? undefined : parseFloat(e.target.value);
                handleInputChange("cargoWeight", value);
              }}
              disabled={loading}
              placeholder={t("admin.cargoWeightPlaceholder")}
              inputProps={{ step: "0.01", min: "0" }}
              helperText={t("admin.cargoWeightHelperText")}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("admin.itemCarrierCargoFee")}
              type="number"
              value={formData.itemCarrierCargoFee ?? ""}
              onChange={(e) => {
                const value = e.target.value === "" ? undefined : parseFloat(e.target.value);
                handleInputChange("itemCarrierCargoFee", value);
              }}
              disabled={loading}
              placeholder={t("admin.itemCarrierCargoFeePlaceholder")}
              inputProps={{ step: "0.01", min: "0" }}
              helperText={t("admin.itemCarrierCargoFeeHelperText")}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t("common.notes")}
              multiline
              rows={3}
              value={formData.notes || ""}
              onChange={(e) => handleInputChange("notes", e.target.value)}
              disabled={loading}
              placeholder={t("admin.itemNotesPlaceholder")}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {t("common.cancel")}
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? t("admin.saving") : t("admin.saveChanges")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrderItemEditModal;
